import type { BadgeGroup, BadgeId, Country, SeasonState } from "../types";

export interface BadgeDef {
  readonly id: BadgeId;
  /** Section the Badges page files this under. */
  readonly group: BadgeGroup;
  // 1 = a simple end-of-season unlock; >1 = a counted in-season challenge,
  // shown with a live progress bar until it completes.
  readonly target: number;
  /** Progress toward unlocking this badge in the given season, 0..target. */
  readonly progress: (s: SeasonState) => number;
}

/** The player's team in a season, or undefined before the draft finalizes. Kept
 *  local to preserve the data layer's independence from engine/ (see selectors). */
const playerTeam = (s: SeasonState) => s.opponents.find((t) => t.isPlayer);

const playerWonWcc = (s: SeasonState): boolean => !!s.standings?.constructors[0]?.isPlayer;
const playerWonWdc = (s: SeasonState): boolean => !!s.standings?.drivers[0]?.isPlayer;
const playerWonAnyTitle = (s: SeasonState): boolean => playerWonWcc(s) || playerWonWdc(s);

/** Number of races the player won outright (a player car finished P1). */
function playerRaceWins(s: SeasonState): number {
  return s.results.filter((r) => r.order.some((e) => e.isPlayer && e.position === 1)).length;
}

/** True once the season is over (final standings exist) and the player won the last `n` races. */
function playerWonFinalRaces(s: SeasonState, n: number): boolean {
  if (!s.standings || s.results.length < n) return false;
  const tail = s.results.slice(s.results.length - n);
  return tail.every((r) => r.order.some((e) => e.isPlayer && e.position === 1));
}

/** Active synergy count on the player's lineup (same as perfect_lineup's measure). */
function playerSynergyCount(s: SeasonState): number {
  return playerTeam(s)?.synergy.reasons.length ?? 0;
}

/** Races where weather wasn't dry and a player car won (finished first). */
function countWetWins(s: SeasonState): number {
  return s.results.filter(
    (r) => r.weather !== "dry" && r.order.some((e) => e.isPlayer && !e.dnf && e.position === 1),
  ).length;
}

/**
 * True if neither of the player's cars ever retired (DNF) across every race of
 * the season. Vacuously true before any race is run, so this is only meaningful
 * paired with a title check (standings exist only at season end).
 */
function playerHadNoDnf(s: SeasonState): boolean {
  return s.results.every((r) => r.order.every((e) => !e.isPlayer || !e.dnf));
}

/** True if the player's two drivers share a nationality. */
function playerSameNation(s: SeasonState): boolean {
  const player = playerTeam(s);
  return !!player && player.d1.country === player.d2.country;
}

/** True if both of the player's drivers are from the given nation. */
function playerNation(s: SeasonState, country: Country): boolean {
  const player = playerTeam(s);
  return !!player && player.d1.country === country && player.d2.country === country;
}

/** A nation challenge: win the Constructors' title with two drivers of `country`. */
const nationBadge = (id: BadgeId, country: Country): BadgeDef => ({
  id,
  group: "nation",
  target: 1,
  progress: (s) => (playerWonWcc(s) && playerNation(s, country) ? 1 : 0),
});

/** True if the player's two cars locked out P1 and P2 in any single race. */
function playerScoredOneTwo(s: SeasonState): boolean {
  return s.results.some(
    (r) =>
      r.order.some((e) => e.isPlayer && e.position === 1) && r.order.some((e) => e.isPlayer && e.position === 2),
  );
}

export const BADGES: readonly BadgeDef[] = [
  {
    id: "goat",
    group: "mastery",
    target: 1,
    progress: (s) => (s.chaos && s.fromMemory && playerWonWcc(s) && playerWonWdc(s) ? 1 : 0),
  },
  { id: "wcc_champion", group: "championship", target: 1, progress: (s) => (playerWonWcc(s) ? 1 : 0) },
  { id: "wdc_champion", group: "championship", target: 1, progress: (s) => (playerWonWdc(s) ? 1 : 0) },
  { id: "chaos_champion", group: "mastery", target: 1, progress: (s) => (s.chaos && playerWonAnyTitle(s) ? 1 : 0) },
  { id: "memory_champion", group: "mastery", target: 1, progress: (s) => (s.fromMemory && playerWonAnyTitle(s) ? 1 : 0) },
  {
    id: "no_mythic_champion",
    group: "mastery",
    target: 1,
    progress: (s) => (s.mythicsPicked === 0 && playerWonAnyTitle(s) ? 1 : 0),
  },
  { id: "rain_master", group: "mastery", target: 2, progress: countWetWins },
  {
    id: "perfect_lineup",
    group: "mastery",
    target: 1,
    progress: (s) => {
      const player = playerTeam(s);
      return player && player.synergy.reasons.length >= 3 ? 1 : 0;
    },
  },
  { id: "grand_slam", group: "championship", target: 1, progress: (s) => (playerWonWcc(s) && playerWonWdc(s) ? 1 : 0) },
  {
    id: "king_of_the_midfield",
    group: "mastery",
    target: 1,
    progress: (s) => {
      if (!playerWonAnyTitle(s)) return 0;
      const player = playerTeam(s);
      if (!player) return 0;
      return player.director?.rarity === "normal" || player.chassis.rarity === "normal" ? 1 : 0;
    },
  },
  { id: "one_two", group: "championship", target: 1, progress: (s) => (playerScoredOneTwo(s) ? 1 : 0) },
  {
    id: "unbreakable",
    group: "championship",
    target: 1,
    progress: (s) => (playerWonWcc(s) && playerHadNoDnf(s) ? 1 : 0),
  },
  {
    id: "total_dominance",
    group: "championship",
    target: 1,
    progress: (s) =>
      playerWonWcc(s) && !!s.standings?.drivers[0]?.isPlayer && !!s.standings?.drivers[1]?.isPlayer ? 1 : 0,
  },
  {
    id: "national_pride",
    group: "nation",
    target: 1,
    progress: (s) => (playerWonWcc(s) && playerSameNation(s) ? 1 : 0),
  },
  // Archetype-flavoured challenges. Outcome-based like rain_master/unbreakable:
  // the matching director strongly FAVOURS each (it's how you'd realistically pull
  // them off), but isn't required — the player discovers the right hire themselves.
  // Developer-favoured: the dev bonus peaks at the finale → close with back-to-back wins.
  {
    id: "late_charge",
    group: "mastery",
    target: 1,
    progress: (s) => (playerWonFinalRaces(s, 2) ? 1 : 0),
  },
  // Strategist-favoured: raw pace converts to race wins. Counted, with a live bar.
  {
    id: "pace_master",
    group: "mastery",
    target: 4,
    progress: (s) => playerRaceWins(s),
  },
  // Motivator-favoured: amplified chemistry → a title with a deeply synergised lineup.
  {
    id: "perfect_harmony",
    group: "mastery",
    target: 1,
    progress: (s) => (playerWonAnyTitle(s) && playerSynergyCount(s) >= 3 ? 1 : 0),
  },
  // Nation challenges — a specialisation of national_pride, pinned to one
  // country. Ordered easy → hard by the strongest legal pairing each nation
  // offers (no duplicate driver identities): the easy tier has a Mythic anchor,
  // the hard tier has nothing above Normal and must be carried by chassis +
  // director. COL and NED are excluded — one card means no same-nation pair.
  nationBadge("nation_bra", "BRA"),
  nationBadge("nation_gbr", "GBR"),
  nationBadge("nation_ger", "GER"),
  nationBadge("nation_fra", "FRA"),
  nationBadge("nation_arg", "ARG"),
  nationBadge("nation_esp", "ESP"),
  nationBadge("nation_fin", "FIN"),
  nationBadge("nation_aus", "AUS"),
  nationBadge("nation_aut", "AUT"),
  nationBadge("nation_ita", "ITA"),
  nationBadge("nation_mex", "MEX"),
  nationBadge("nation_can", "CAN"),
];
